/**
 * useResourceProgress Hook
 *
 * Tracks progress for library resources:
 * - PDF/Doc: scroll progress and time spent
 * - Video/Audio: media playback progress
 * - Completion detection based on resource type
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  getResourceProgress,
  updateResourceViewProgress,
  markResourceCompleted,
} from '@/services/readingProgress';
import { incrementMetric } from '@/services/engagementMetrics';
import {
  isResourceComplete,
  type ResourceReadingProgress,
  type ResourceProgress,
} from '@/types/reading';
import type { FileType } from '@/types/library';

// ============================================
// CONSTANTS
// ============================================

const PROGRESS_UPDATE_INTERVAL_MS = 5000; // Update progress every 5 seconds
const COMPLETION_THRESHOLD_SCROLL = 80; // 80% scroll = complete for PDFs
const COMPLETION_THRESHOLD_MEDIA = 80; // 80% playback = complete for media
const ESTIMATED_READING_TIME_PER_PAGE_MS = 2 * 60 * 1000; // 2 minutes per page

// ============================================
// TYPES
// ============================================

export interface ResourceProgressState {
  progress: ResourceProgress | null;
  isLoading: boolean;
  isCompleted: boolean;
  currentSessionTimeMs: number;
  scrollProgress: number;
  mediaProgress: number;
}

export interface ResourceProgressCallbacks {
  onResourceComplete?: (resourceTitle: string, isFirstResource: boolean) => void;
}

export interface UseResourceProgressOptions {
  resourceId: string;
  fileId: string;
  title: string;
  type: FileType;
  totalPages?: number; // For PDFs
  callbacks?: ResourceProgressCallbacks;
  enabled?: boolean;
}

export interface UseResourceProgressReturn {
  state: ResourceProgressState;
  reportScrollProgress: (progress: number) => void;
  reportMediaProgress: (progress: number) => void;
  reportPageChange: (currentPage: number, totalPages: number) => void;
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

export function useResourceProgress({
  resourceId,
  fileId,
  title,
  type,
  totalPages,
  callbacks,
  enabled = true,
}: UseResourceProgressOptions): UseResourceProgressReturn {
  const { authState } = useAuth();
  const userId = authState.status === 'authenticated' ? authState.user.uid : null;

  // State
  const [resourceProgressDoc, setResourceProgressDoc] = useState<ResourceReadingProgress | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [currentSessionTimeMs, setCurrentSessionTimeMs] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mediaProgress, setMediaProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  // Refs
  const sessionStartTimeRef = useRef<number>(Date.now());
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const hasMarkedCompleteRef = useRef(false);
  const maxScrollProgressRef = useRef(0);
  const maxMediaProgressRef = useRef(0);
  const currentSessionTimeMsRef = useRef(0);

  // Get current resource progress
  const currentProgress = resourceProgressDoc?.resources[resourceId] || null;

  // Initialize on mount
  useEffect(() => {
    if (!userId || !resourceId || !enabled) return;

    const loadProgress = async () => {
      setIsLoading(true);
      try {
        const progress = await getResourceProgress(userId);
        setResourceProgressDoc(progress);

        const existing = progress.resources[resourceId];
        if (existing) {
          maxScrollProgressRef.current = existing.scrollProgress || 0;
          maxMediaProgressRef.current = existing.mediaProgress || 0;
          setScrollProgress(existing.scrollProgress || 0);
          setMediaProgress(existing.mediaProgress || 0);
          setIsCompleted(!!existing.completedAt);
          hasMarkedCompleteRef.current = !!existing.completedAt;
        }
      } catch (error) {
        console.error('Error loading resource progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [userId, enabled, resourceId]);

  // Track view time
  useEffect(() => {
    if (!userId || !resourceId || !enabled) return;

    const interval = setInterval(async () => {
      const now = Date.now();
      const elapsed = now - lastUpdateTimeRef.current;
      lastUpdateTimeRef.current = now;

      setCurrentSessionTimeMs((prev) => {
        const next = prev + elapsed;
        currentSessionTimeMsRef.current = next;
        return next;
      });

      try {
        await updateResourceViewProgress(userId, resourceId, fileId, title, type, {
          additionalTimeMs: elapsed,
          scrollProgress: maxScrollProgressRef.current,
          mediaProgress: maxMediaProgressRef.current,
        });

        // Check for completion
        await checkCompletion();
      } catch (error) {
        console.error('Error updating resource progress:', error);
      }
    }, PROGRESS_UPDATE_INTERVAL_MS);

    // Mark as new view on mount
    const trackNewView = async () => {
      try {
        const { isNewView } = await updateResourceViewProgress(
          userId,
          resourceId,
          fileId,
          title,
          type,
          { additionalTimeMs: 0 }
        );

        if (isNewView) {
          await incrementMetric(userId, 'resourcesViewedThisWeek', 1);
        }
      } catch (error) {
        console.error('Error tracking new view:', error);
      }
    };

    trackNewView();

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkCompletion excluded: adding it causes re-render loops
  }, [userId, enabled, resourceId, fileId, title, type]);

  // Check for completion - uses ref for currentSessionTimeMs to avoid
  // re-creating on every interval tick (which would cause re-render loops)
  const checkCompletion = useCallback(async () => {
    if (!userId || !resourceId || hasMarkedCompleteRef.current) return;

    const estimatedReadingTimeMs = totalPages
      ? totalPages * ESTIMATED_READING_TIME_PER_PAGE_MS
      : undefined;

    const progressData: ResourceProgress = {
      fileId,
      title,
      type,
      viewCount: 1,
      totalViewTimeMs: currentSessionTimeMsRef.current,
      scrollProgress: maxScrollProgressRef.current,
      mediaProgress: maxMediaProgressRef.current,
      lastViewedAt: new Date() as unknown as import('firebase/firestore').Timestamp,
    };

    if (isResourceComplete(progressData, estimatedReadingTimeMs)) {
      hasMarkedCompleteRef.current = true;
      setIsCompleted(true);

      try {
        const { isFirstResource } = await markResourceCompleted(userId, resourceId);
        callbacks?.onResourceComplete?.(title, isFirstResource);
      } catch (error) {
        console.error('Error marking resource complete:', error);
        hasMarkedCompleteRef.current = false;
        setIsCompleted(false);
      }
    }
  }, [userId, resourceId, fileId, title, type, totalPages, callbacks]);

  // Report scroll progress (for PDFs/docs)
  const reportScrollProgress = useCallback(
    (progress: number) => {
      const normalizedProgress = Math.min(100, Math.max(0, progress));
      setScrollProgress(normalizedProgress);

      if (normalizedProgress > maxScrollProgressRef.current) {
        maxScrollProgressRef.current = normalizedProgress;
      }

      // Check for completion threshold
      if (normalizedProgress >= COMPLETION_THRESHOLD_SCROLL) {
        checkCompletion();
      }
    },
    [checkCompletion]
  );

  // Report media progress (for video/audio)
  const reportMediaProgress = useCallback(
    (progress: number) => {
      const normalizedProgress = Math.min(100, Math.max(0, progress));
      setMediaProgress(normalizedProgress);

      if (normalizedProgress > maxMediaProgressRef.current) {
        maxMediaProgressRef.current = normalizedProgress;
      }

      // Check for completion threshold
      if (normalizedProgress >= COMPLETION_THRESHOLD_MEDIA) {
        checkCompletion();
      }
    },
    [checkCompletion]
  );

  // Report page change (convenience method for PDFs)
  const reportPageChange = useCallback(
    (currentPage: number, totalPages: number) => {
      if (totalPages > 0) {
        const progress = (currentPage / totalPages) * 100;
        reportScrollProgress(progress);
      }
    },
    [reportScrollProgress]
  );

  // Cleanup on unmount - save final progress
  useEffect(() => {
    if (!userId || !resourceId || !enabled) return;

    // Capture ref values at effect setup time for safe cleanup
    const sessionStartTime = sessionStartTimeRef.current;
    const maxScrollProgress = maxScrollProgressRef.current;
    const maxMediaProgress = maxMediaProgressRef.current;

    return () => {
      const sessionTimeMs = Date.now() - sessionStartTime;

      updateResourceViewProgress(userId, resourceId, fileId, title, type, {
        additionalTimeMs: sessionTimeMs - currentSessionTimeMs,
        scrollProgress: maxScrollProgress,
        mediaProgress: maxMediaProgress,
      }).catch((error) => {
        console.error('Error saving final resource progress:', error);
      });
    };
  }, [userId, enabled, resourceId, fileId, title, type, currentSessionTimeMs]);

  return {
    state: {
      progress: currentProgress,
      isLoading,
      isCompleted,
      currentSessionTimeMs,
      scrollProgress,
      mediaProgress,
    },
    reportScrollProgress,
    reportMediaProgress,
    reportPageChange,
  };
}

export default useResourceProgress;
